import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import validator from 'validator';
import useTable from '../../components/useTable';
import UnassignUserButton from './UnassignUserButton';
import DeleteUserButton from './DeleteUserButton';
import { useApiClient, convertAxiosErrorToUserFacingMessage } from '../../context/ApiClientContext';
import { RolesDropdown, RolesProvider } from '../../components/RoleManager';
import usePermission from '../../hooks/usePermissions';
import UserAssignedSitesModalButton from './UserAssignedSitesModalButton';
import AddSitesModalButton from './AddSitesModalButton';
import SiteFilterDropdown from './SiteFilterDropdown';

const headers = [
  { id: 'username', label: 'Username' },
  { id: 'roles', label: 'Roles', disableSorting: true },
  { id: 'phoneNumber', label: 'Phone Number', disableSorting: true },
  { id: 'mfaMethod', label: 'MFA Method', disableSorting: true },
  { id: 'sites', label: 'Sites', disableSorting: true },
  { id: 'assignSites', label: '', disableSorting: true },
  { id: 'edit', label: '', disableSorting: true },
];

export default function UserInfoTable(props) {
  const apiClient = useApiClient();
  const {
    data,
    siteId,
    siteName,
    fetchUsers,
    setSuccessMessage,
    setErrorMessage,
    canDeleteUsers,
    showSitesColumn,
    totalSiteCount,
    allSites,
  } = props;

  const [editingRoles, setEditingRoles] = useState(null);
  const [editingUserPhoneNumber, setEditingUserPhoneNumber] = useState(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [editingMfaMethod, setEditingMfaMethod] = useState({});
  const [userSitesModal, setUserSitesModal] = useState(null);
  const [userAssignSitesModal, setUserAssignSitesModal] = useState(null);
  const [selectedSiteFilter, setSelectedSiteFilter] = useState('');

  const {
    isAdmin,
    isCustomerAdmin,
    hasAccuroAccess,
  } = usePermission();

  // Filter users based on selected site
  const filteredData = useMemo(() => {
    if (!selectedSiteFilter) return data;

    return data.filter((user) => {
      if (user.siteIds.includes('*')) return true;
      return user.siteIds.includes(selectedSiteFilter);
    });
  }, [data, selectedSiteFilter]);

  const {
    TblContainer,
    TblHead,
    TblPagination,
    currentPageDataSorted,
  } = useTable(filteredData, headers, { fn: (items) => items });

  const handleEditRoles = (user) => {
    setEditingRoles(user);
    setSelectedRoles(user.roles);
  };

  const handleRolesChange = (e) => {
    setSelectedRoles(e.target.value);
  };

  const handleCancelRoles = () => {
    setEditingRoles(null);
  };

  const handleEditPhoneNumber = (user) => {
    setEditingUserPhoneNumber(user);
    setNewPhoneNumber(user.phoneNumber);
  };

  const handleCancelPhoneNumber = () => {
    setEditingUserPhoneNumber(null);
    setNewPhoneNumber('');
  };

  const handleMfaChange = async (user, method) => {
    setEditingMfaMethod((prev) => ({
      ...prev,
      [user.username]: method,
    }));

    try {
      await apiClient.updateMfaMethod(user.username, method);
      setSuccessMessage('MFA method updated successfully.');
    } catch (error) {
      setErrorMessage('Failed to update MFA method.');
      setEditingMfaMethod((prev) => ({
        ...prev,
        [user.username]: user.mfaMethod, // Revert to the original value from the user object
      }));
    }
  };

  const handleSave = async (user) => {
    const userChanges = {};

    if (editingRoles === user) {
      userChanges.roles = selectedRoles;
    }

    if (editingUserPhoneNumber === user) {
      if (!newPhoneNumber || !validator.isMobilePhone(newPhoneNumber)) {
        setErrorMessage('Invalid phone number.');
        return;
      }
      userChanges.phoneNumber = newPhoneNumber;
    }
    try {
      await apiClient.updateUser(user.username, userChanges);
      setSuccessMessage('User updated successfully.');
      fetchUsers();
    } catch (error) {
      setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
    }
    setEditingRoles(null);
    setEditingUserPhoneNumber(null);
    setNewPhoneNumber('');
    setEditingMfaMethod({});
  };

  const isValidEmail = (email) => validator.isEmail(email);

  return (
    <RolesProvider>
      {showSitesColumn && allSites.length > 0 && (
        <SiteFilterDropdown
          selectedSite={selectedSiteFilter}
          onSiteChange={setSelectedSiteFilter}
          sites={allSites}
        />
      )}
      <TblContainer>
        <TblHead />
        <TableBody>
          {currentPageDataSorted().map((user) => (
            <TableRow key={user.username}>
              <TableCell>{user.username}</TableCell>
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <RolesDropdown
                    isEditing={editingRoles === user}
                    isAdmin={isAdmin()}
                    isCustomerAdmin={isCustomerAdmin()}
                    hasAccuroAccess={hasAccuroAccess()}
                    user={user}
                    name="roles"
                    handleInputChange={handleRolesChange}
                    selectedRoles={selectedRoles}
                  />
                  {editingRoles === user ? (
                    <>
                      <Button onClick={() => handleSave(user)}>Save</Button>
                      <Button onClick={handleCancelRoles}>Cancel</Button>
                    </>
                  ) : (
                    <Tooltip title="Edit Roles">
                      <IconButton
                        style={{ marginLeft: '10px' }}
                        onClick={() => handleEditRoles(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {editingUserPhoneNumber === user ? (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <PhoneInput
                      country="ca"
                      value={newPhoneNumber}
                      preferredCountries={['us', 'ca', 'ae', 'sa']}
                      onBlur={(event) => setNewPhoneNumber(event.target.value)}
                      placeholder="Enter phone number"
                      inputProps={{
                        style: { margin: '10px 0px' },
                        'data-testid': 'phoneNumber',
                      }}
                    />
                    <Button
                      onClick={() => {
                        handleSave(user);
                      }}
                    >
                      Save
                    </Button>
                    <Button onClick={handleCancelPhoneNumber}>Cancel</Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>{user.phoneNumber || 'No phone number'}</div>
                    <Tooltip title="Edit Phone Number">
                      <IconButton
                        style={{ marginLeft: '10px' }}
                        onClick={() => handleEditPhoneNumber(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Grid item>
                  <RadioGroup
                    row
                    value={editingMfaMethod[user.username] || user.mfaMethod}
                    onChange={(event) => handleMfaChange(user, event.target.value)}
                  >
                    <FormControlLabel value="NONE" control={<Radio color="primary" />} label="None" disabled={!isAdmin()} />
                    <FormControlLabel value="SMS" control={<Radio color="primary" />} label="SMS" disabled={!user.phoneNumber} />
                    <FormControlLabel value="EMAIL" control={<Radio color="primary" />} label="Email" disabled={!isValidEmail(user.username)} />
                  </RadioGroup>
                </Grid>
              </TableCell>
              {showSitesColumn && (
                <>
                  <TableCell>
                    <UserAssignedSitesModalButton
                      user={user}
                      fetchUsers={fetchUsers}
                      isOpen={userSitesModal === user.username}
                      onOpen={() => setUserSitesModal(user.username)}
                      onClose={() => setUserSitesModal(null)}
                      totalSiteCount={totalSiteCount}
                      setSuccessMessage={setSuccessMessage}
                      setErrorMessage={setErrorMessage}
                      allSites={allSites}
                    />
                  </TableCell>
                  <TableCell>
                    <AddSitesModalButton
                      user={user}
                      fetchUsers={fetchUsers}
                      isOpen={userAssignSitesModal === user.username}
                      onOpen={() => setUserAssignSitesModal(user.username)}
                      onClose={() => setUserAssignSitesModal(null)}
                      setSuccessMessage={setSuccessMessage}
                      setErrorMessage={setErrorMessage}
                      allSites={allSites}
                    />
                  </TableCell>
                </>
              )}
              <TableCell>
                {canDeleteUsers && (
                  <DeleteUserButton
                    username={user.username}
                    fetchUsers={fetchUsers}
                    setSuccessMessage={setSuccessMessage}
                    setErrorMessage={setErrorMessage}
                  />
                )}
                {!canDeleteUsers && siteId && siteName && (
                  <UnassignUserButton
                    username={user.username}
                    siteId={siteId}
                    siteName={siteName}
                    fetchUsers={fetchUsers}
                    setSuccessMessage={setSuccessMessage}
                    setErrorMessage={setErrorMessage}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TblContainer>
      <TblPagination />
    </RolesProvider>
  );
}

UserInfoTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string.isRequired,
      phoneNumber: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string)
        .isRequired,
    }),
  ).isRequired,
  siteId: PropTypes.string,
  siteName: PropTypes.string,
  fetchUsers: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  canDeleteUsers: PropTypes.bool,
  showSitesColumn: PropTypes.bool,
  totalSiteCount: PropTypes.number,
  allSites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

UserInfoTable.defaultProps = {
  canDeleteUsers: false,
  showSitesColumn: false,
  siteId: '',
  siteName: '',
  totalSiteCount: 0,
  allSites: [],
};
